import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import ClientCard from './ClientCard';
import AddClientForm from './AddClientForm';
import DeleteConfirmModal from './DeleteConfirmModal';
import '../styles/Dashboard.css';
import ArchivedClientsModal from './ArchivedClientsModal';
import EditClientModal from './EditClientModal';
import AddUserModal from './AddUserModal';
import ManageUsersModal from './ManageUsersModal';
import ChangePasswordModal from './ChangePasswordModal';
import SettingsPage from './SettingsPage';
import DataDeletionPage from './DataDeletionPage';
import FeedbackWidget from './FeedbackWidget';
import BetaUpdatesPanel from './BetaUpdatesPanel';

function Dashboard({ user, org, onLogout }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, client: null });
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [editModal, setEditModal] = useState({ show: false, client: null });
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showManageUsersModal, setShowManageUsersModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [datePickerClient, setDatePickerClient] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [generating, setGenerating] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDataDeletion, setShowDataDeletion] = useState(false);
  const [pendingFeedbackCount, setPendingFeedbackCount] = useState(0);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [showBetaUpdates, setShowBetaUpdates] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [approvingId, setApprovingId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [clientStats, setClientStats] = useState({});
  const actionsDropdownRef = useRef(null);
  const settingsDropdownRef = useRef(null);

  // Load clients + summary on mount
  useEffect(() => {
    loadClients();
    axios.get(`${process.env.REACT_APP_API_URL}/api/dashboard/summary`, { withCredentials: true })
      .then(res => setSummary(res.data))
      .catch(err => console.error('Failed to load dashboard summary:', err));
    axios.get(`${process.env.REACT_APP_API_URL}/api/dashboard/client-stats`, { withCredentials: true })
      .then(res => setClientStats(res.data.stats || {}))
      .catch(err => console.error('Failed to load client stats:', err));
  }, []);

  // Poll pending feedback count for superadmin
  useEffect(() => {
    if (user.role !== 'superadmin') return;
    const fetchPendingCount = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/feedback/pending-count`, {
          withCredentials: true
        });
        setPendingFeedbackCount(res.data.count);
      } catch (err) {
        // silently ignore
      }
    };
    fetchPendingCount();
  }, [user.role]);

  // Listen for refresh events from child components (e.g. after report approval)
  useEffect(() => {
    const handleRefresh = () => loadClients();
    window.addEventListener('refreshClients', handleRefresh);
    return () => window.removeEventListener('refreshClients', handleRefresh);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(e.target)) {
        setShowActionsDropdown(false);
      }
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(e.target)) {
        setShowSettingsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const refreshSummary = () => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/dashboard/summary`, { withCredentials: true })
      .then(res => setSummary(res.data))
      .catch(err => console.error('Failed to refresh dashboard summary:', err));
  };

  const loadClients = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, {
        withCredentials: true
      });
      setClients(response.data.clients);
    } catch (err) {
      setError('Failed to load clients');
      toast.error('Failed to load clients');
      console.error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClientAdded = (newClient) => {
    setShowAddForm(false);
    loadClients();
    toast.success(`✅ ${newClient.name} added successfully!`);
  };

  const handleDeleteClick = (client) => {
    setDeleteModal({ show: true, client });
  };

    const handleDeleteConfirm = async () => {
        const client = deleteModal.client;
        setDeleteModal({ show: false, client: null });

        try {
          const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/clients/${client.id}`, {
            withCredentials: true
          });

            if (response.data.success) {
                // Show different message based on archive vs delete
                if (response.data.type === 'archived') {
                  toast.success(
                    `📦 ${client.name} archived`,
                    { duration: 4000 }
                  );
                    console.log('Data preserved:', response.data.dataPreserved);
                } else {
                  toast.success(
                    `🗑️ ${client.name} deleted`,
                    { duration: 3000 }
                  );
                }

                loadClients();
            }
        } catch (err) {
            toast.error('Failed to delete client');
            console.error('Error deleting client:', err);
        }
    };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, client: null });
  };

  const handleEditClick = (client, options = {}) => {
    setEditModal({ show: true, client, autoConnect: options.autoConnect || false });
  };

  const handleEditClose = () => {
    setEditModal({ show: false, client: null });
  };

  const handleClientUpdated = () => {
    setEditModal({ show: false, client: null });
    loadClients(); // Refresh list
  };

  const handleOpenDatePicker = (client) => {
    setDateRange({
      startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0]
    });
    setDatePickerClient(client);
  };

  const handleGenerateReport = async () => {
    if (!datePickerClient) return;
    setGenerating(true);
    try {
      // Pre-check: warn if client has no collected data for the selected period
      try {
        const availRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/data-availability/${datePickerClient.id}`,
          { withCredentials: true }
        );
        const avail = availRes.data;
        if (!avail.hasInstagramData && !avail.hasFacebookData) {
          const proceed = window.confirm(
            `⚠️ No analytics data found for ${datePickerClient.name} in this date range.\n\nThe report will generate but most metrics will show zero. This usually means the account was just connected and data hasn't been collected yet.\n\nProceed anyway?`
          );
          if (!proceed) {
            setGenerating(false);
            return;
          }
        }
      } catch {
        // Data-availability check failed — don't block report generation
      }

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/reports/generate`, {
        clientId: datePickerClient.id,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      }, { withCredentials: true });

      if (response.data.success) {
        toast.success('📄 Report generated successfully!');
        window.open(response.data.downloadUrl, '_blank');
        setDatePickerClient(null);
      }
    } catch (err) {
      toast.error('Failed to generate report');
      console.error('Error generating report:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleCheckReportsNow = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/check-reports-now`, {}, {
        withCredentials: true
      });
      toast.success('📋 Report check started! Refresh in a moment.');
    } catch (err) {
      toast.error('Failed to trigger report check');
    }
  };

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/feedback/summary`, {}, {
        withCredentials: true
      });
      toast.success(`📧 Summary of ${res.data.count} submission${res.data.count === 1 ? '' : 's'} emailed to you!`);
      setPendingFeedbackCount(0);
    } catch (err) {
      toast.error('Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handlePreviewReport = (report) => {
    const filename = report.report_path.split('/').pop();
    window.open(`${process.env.REACT_APP_API_URL}/reports/${filename}`, '_blank');
  };

  const handleApproveReport = async (report) => {
    setApprovingId(report.id);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/reports/${report.id}/approve`,
        {},
        { withCredentials: true }
      );
      if (response.data.success) {
        toast.success(`📧 Report sent to ${report.clientEmail}!`);
        loadClients();
        refreshSummary();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send report');
    } finally {
      setApprovingId(null);
    }
  };

  const handleDismissReport = async (report) => {
    if (!window.confirm('Dismiss this report? It will not be sent. Auto-reporting will continue as scheduled.')) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/reports/${report.id}/dismiss`,
        {},
        { withCredentials: true }
      );
      if (response.data.success) {
        toast.success('Report dismissed — auto-reporting continues as scheduled');
        loadClients();
        refreshSummary();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to dismiss report');
    }
  };

  const handleCollectNow = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/collect-now`, {}, {
        withCredentials: true
      });
      toast.success('🤖 Data collection started! Check back in a minute.');
    } catch (err) {
      toast.error('Failed to trigger collection');
    }
  };

  return (
    <div className="dashboard">
      {/* Settings Page — full-screen overlay when active */}
      {showSettings && (
        <SettingsPage user={user} org={org} onClose={() => setShowSettings(false)} />
      )}

      {/* Data Deletion Requests — superadmin only, full-screen overlay when active */}
      {showDataDeletion && (
        <DataDeletionPage org={org} onClose={() => setShowDataDeletion(false)} />
      )}

      {/* Beta Updates Panel */}
      {showBetaUpdates && (
        <BetaUpdatesPanel onClose={() => setShowBetaUpdates(false)} />
      )}

      {/* Toast Notifications Container */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
            padding: '16px',
            borderRadius: '8px',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <DeleteConfirmModal
          clientName={deleteModal.client.name}
          hasData={deleteModal.client.has_data}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}

      {/* Archived Clients Modal */}
      {showArchivedModal && (
        <ArchivedClientsModal
          onClose={() => setShowArchivedModal(false)}
          onRestore={loadClients}
        />
      )}

      {/* Edit Client Modal */}
      {editModal.show && (
        <EditClientModal
          client={editModal.client}
          onClose={handleEditClose}
          onClientUpdated={handleClientUpdated}
          autoConnect={editModal.autoConnect}
        />
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <AddUserModal
          onClose={() => setShowAddUserModal(false)}
          onUserAdded={() => {
            toast.success('User added successfully!');
          }}
          currentUserRole={user.role}
        />
      )}

      {/* Manage Users Modal */}
      {showManageUsersModal && (
        <ManageUsersModal
          onClose={() => setShowManageUsersModal(false)}
          currentUserRole={user.role}
        />
      )}
      {/* Date Picker Modal */}
      {datePickerClient && (
        <div
          onClick={() => setDatePickerClient(null)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              width: '400px',
              maxWidth: 'calc(100vw - 40px)',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <div className="edit-modal-header" style={{ borderRadius: '12px 12px 0 0' }}>
              <h2 style={{ fontSize: '18px' }}>📅 Generate Report</h2>
              <button onClick={() => setDatePickerClient(null)} className="close-button">✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
                Select the date range for <strong>{datePickerClient.name}</strong>'s report. Defaults to the last 90 days.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div className="date-picker-field">
                  <label>From</label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  />
                </div>
                <div className="date-picker-field">
                  <label>To</label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="edit-form-actions" style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6' }}>
              <button onClick={() => setDatePickerClient(null)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleGenerateReport} disabled={generating} className="btn-primary">
                {generating ? '⏳ Generating...' : '📄 Generate Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowChangePasswordModal(false)}
        />
      )}

      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="header-brand">
            <div className="org-name">{org ? org.name : 'ReportFlow'}</div>
            <div className="org-sub">Analytics Dashboard</div>
          </div>
          <nav className="dashboard-nav">
            <span className="nav-item active">Clients</span>
            <span className="nav-item inactive">Reports <span className="nav-soon">soon</span></span>
          </nav>
        </div>
        <div className="header-right">
          <span className="user-welcome">{user._welcomeMsg}</span>
          <div className="settings-dropdown-wrapper" ref={settingsDropdownRef}>
            <button
              className="btn-settings-new"
              onClick={() => setShowSettingsDropdown(o => !o)}
            >
              Settings <span className={`dropdown-caret ${showSettingsDropdown ? 'open' : ''}`}>▾</span>
            </button>
            {showSettingsDropdown && (
              <div className="dropdown-menu settings-dropdown">
                <button
                  className="dropdown-item"
                  onClick={() => { setShowChangePasswordModal(true); setShowSettingsDropdown(false); }}
                >
                  Change Password
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => { setShowSettings(true); setShowSettingsDropdown(false); }}
                >
                  PDF Report Builder <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 'normal' }}>(Beta)</span>
                </button>
                {user.role === 'superadmin' && (
                  <button
                    className="dropdown-item"
                    onClick={() => { setShowDataDeletion(true); setShowSettingsDropdown(false); }}
                  >
                    Data Deletion Requests
                  </button>
                )}
              </div>
            )}
          </div>
          <button onClick={onLogout} className="btn-logout">
            Log Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">

        {/* Summary Bar */}
        {summary && (
          <div className="summary-bar">
            <div className="summary-card c-orange">
              <div className="summary-label">Active Clients</div>
              <div className="summary-value">{summary.activeClients}</div>
              <div className="summary-sub">{org ? org.name : 'ReportFlow'}</div>
            </div>
            <div className="summary-card c-blue">
              <div className="summary-label">Reports This Month</div>
              <div className="summary-value">{summary.reportsThisMonth}</div>
              <div className="summary-sub">
                {summary.pendingCount > 0
                  ? <span className="summary-chip chip-orange">{summary.pendingCount} pending approval</span>
                  : <span style={{ color: 'var(--text-muted)' }}>None pending</span>
                }
              </div>
            </div>
            <div className="summary-card c-purple">
              <div className="summary-label">Next Scheduled</div>
              <div className="summary-value">
                {summary.nextScheduled
                  ? new Date(summary.nextScheduled.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : '—'}
              </div>
              <div className="summary-sub">
                {summary.nextScheduled ? summary.nextScheduled.clientName : 'No reports scheduled'}
              </div>
            </div>
            <div className="summary-card c-green">
              <div className="summary-label">Connected</div>
              <div className="summary-value">
                {summary.connected}
                <span className="summary-value-total"> / {summary.totalClients}</span>
              </div>
              <div className="summary-sub">
                {summary.needsAttention > 0
                  ? <span className="summary-chip chip-red">{summary.needsAttention} needs attention</span>
                  : <span style={{ color: 'var(--text-muted)' }}>All connected</span>
                }
              </div>
            </div>
          </div>
        )}

        {/* Superadmin: pending feedback indicator */}
        {user.role === 'superadmin' && pendingFeedbackCount > 0 && (
          <div className="feedback-pending-banner">
            <div className="feedback-pending-left">
              <span className="feedback-pending-dot" />
              <span className="feedback-pending-text">
                <strong>{pendingFeedbackCount}</strong> unreviewed beta feedback submission{pendingFeedbackCount === 1 ? '' : 's'}
              </span>
            </div>
            <button
              className="feedback-summary-btn"
              onClick={handleGenerateSummary}
              disabled={generatingSummary}
            >
              {generatingSummary ? 'Generating...' : '📧 Generate & Email Summary'}
            </button>
          </div>
        )}

        {/* Pending Review Section */}
        {(() => {
          const pendingReports = clients
            .filter(c => c.pending_report)
            .map(c => ({ ...c.pending_report, clientName: c.name, clientEmail: c.client_email }));

          if (pendingReports.length === 0) return null;

          return (
            <div className="section">
              <div className="section-header">
                <h2>Pending Review</h2>
              </div>
              <div className="pending-list">
                {pendingReports.map(report => {
                  const createdAt = new Date(report.created_at);
                  const dateLabel = createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const timeLabel = createdAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                  let period = '';
                  if (report.start_date && report.end_date) {
                    const start = new Date(report.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    const end = new Date(report.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    period = `${start} – ${end}`;
                  }
                  return (
                    <div key={report.id} className="pending-row">
                      <div className="pending-left">
                        <div className="pending-pulse" />
                        <div>
                          <div className="pending-name">
                            {report.clientName}{period ? ` — ${period}` : ''}
                          </div>
                          <div className="pending-meta">Generated {dateLabel} at {timeLabel} · Ready to send</div>
                        </div>
                      </div>
                      <div className="pending-btns">
                        <button className="pending-btn pending-btn-preview" onClick={() => handlePreviewReport(report)}>
                          👁 Preview
                        </button>
                        <button className="pending-btn pending-btn-dismiss" onClick={() => handleDismissReport(report)}>
                          Dismiss
                        </button>
                        <button
                          className="pending-btn pending-btn-approve"
                          onClick={() => handleApproveReport(report)}
                          disabled={approvingId === report.id}
                        >
                          {approvingId === report.id ? 'Sending...' : '✓ Approve & Send'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Clients Section */}
        <div className="section">
          <div className="section-header">
            <h2>Your Clients</h2>
            <div className="header-actions">
              <div className="search-wrapper">
                <input
                  className="client-search"
                  type="text"
                  placeholder="Search clients…"
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                />
                {clientSearch && (
                  <button className="search-clear" onClick={() => setClientSearch('')}>×</button>
                )}
              </div>
              <div className="actions-dropdown-wrapper" ref={actionsDropdownRef}>
                <button
                  className="btn-primary"
                  onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                >
                  ⚡ Actions <span className={`dropdown-caret ${showActionsDropdown ? 'open' : ''}`}>▾</span>
                </button>
                {showActionsDropdown && (
                  <div className="dropdown-menu actions-dropdown">
                    <button
                      onClick={() => { setShowAddForm(!showAddForm); setShowActionsDropdown(false); }}
                      className="dropdown-item"
                    >
                      ➕ Add Client
                    </button>
                    {(user.role === 'admin' || user.role === 'superadmin') && (
                      <>
                        <div className="dropdown-divider" />
                        <button
                          onClick={() => { setShowAddUserModal(true); setShowActionsDropdown(false); }}
                          className="dropdown-item"
                        >
                          👤 Add User
                        </button>
                        <button
                          onClick={() => { setShowManageUsersModal(true); setShowActionsDropdown(false); }}
                          className="dropdown-item"
                        >
                          👥 Manage Users
                        </button>
                        <button
                          onClick={() => { setShowArchivedModal(true); setShowActionsDropdown(false); }}
                          className="dropdown-item"
                        >
                          📦 View Archived
                        </button>
                      </>
                    )}
                    {user.role === 'superadmin' && (
                      <>
                        <div className="dropdown-divider" />
                        <button
                          onClick={() => { handleCollectNow(); setShowActionsDropdown(false); }}
                          className="dropdown-item"
                        >
                          🤖 Collect Now
                        </button>
                        <button
                          onClick={() => { handleCheckReportsNow(); setShowActionsDropdown(false); }}
                          className="dropdown-item"
                        >
                          📋 Check Reports Now
                        </button>
                        <button
                          onClick={() => { setShowBetaUpdates(true); setShowActionsDropdown(false); }}
                          className="dropdown-item"
                        >
                          📧 Beta Updates
                        </button>
                        <button
                          onClick={() => { window.open(`${process.env.REACT_APP_API_URL}/api/admin/org-overview`, '_blank'); setShowActionsDropdown(false); }}
                          className="dropdown-item"
                        >
                          🔍 Org Overview
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Add Client Form */}
          {showAddForm && (
            <AddClientForm 
              userId={user.id}
              onClientAdded={handleClientAdded}
              onCancel={() => setShowAddForm(false)}
            />
          )}

          {/* Loading Skeletons */}
          {loading && (
            <div className="clients-grid">
              {[1, 2, 3].map(i => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton-header"></div>
                  <div className="skeleton-badge"></div>
                  <div className="skeleton-button"></div>
                  <div className="skeleton-button"></div>
                </div>
              ))}
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="error-box">
              {error}
            </div>
          )}

          {/* Empty State with Illustration */}
          {!loading && !error && clients.length === 0 && (
            <div className="empty-state">
              <div className="empty-illustration">
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                  <circle cx="60" cy="60" r="50" stroke="#e0e0e0" strokeWidth="4" strokeDasharray="8 8"/>
                  <path d="M40 60 L50 70 L80 40" stroke="#ec6433" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>No clients yet</h3>
              <p>Click "Add Client" above to get started!</p>
            </div>
          )}

          {/* Clients Grid */}
          {!loading && !error && clients.length > 0 && (
            <div className="clients-grid">
              {clients
                .filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase()))
                .map(client => (
                  <ClientCard
                    key={client.id}
                    client={client}
                    stats={clientStats[client.id] || null}
                    onDelete={handleDeleteClick}
                    onEdit={handleEditClick}
                    onGenerateReport={handleOpenDatePicker}
                    userRole={user.role}
                  />
                ))}
            </div>
          )}
        </div>

        {/* Beta Feedback Widget */}
        <FeedbackWidget user={user} />

      </div>
    </div>
  );
}

export default Dashboard;